import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  Popover,
  PopoverPanel,
  PopoverButton,
  Transition,
} from '@headlessui/react';
import { useController } from 'react-hook-form';

const pad = (n) => String(n).padStart(2, '0');

const toMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const TimeColumn = ({ items, selected, onSelect, isDisabled }) => {
  const refs = useRef({});

  useEffect(() => {
    if (refs.current[selected]) {
      refs.current[selected].scrollIntoView({ block: 'center' });
    }
  }, [selected]);

  return (
    <div className="h-48 w-16 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-inner snap-y">
      <ul className="py-2">
        {items.map((v) => {
          const disabled = isDisabled?.(v) ?? false;
          const isSel = v === selected;

          return (
            <li
              key={v}
              ref={(el) => (refs.current[v] = el)}
              onClick={() => {
                if (!disabled) onSelect(v);
              }}
              className={`px-3 py-2 text-sm text-center snap-start
                ${disabled ? 'text-gray-300 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-100 hover:rounded-md hover:mx-1'}
                ${isSel ? 'bg-yellow-05 hover:bg-yellow-05/70 text-white font-semibold rounded-md mx-1' : ''}`}
            >
              {pad(v)}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const TimePicker = ({
  name,
  control,
  rules,
  placeholder = '--:--',
  stepMinutes = 5,
  disabled = false,
  className = '',
  minTime,
  maxTime,
  minExclusive = false,
  maxExclusive = false,
}) => {
  const { field, fieldState } = useController({ name, control, rules });

  const [openHour, openMinute] = useMemo(() => {
    if (!field.value) return [null, null];
    const [h, m] = String(field.value)
      .split(':')
      .map((x) => parseInt(x, 10));
    return [isNaN(h) ? null : h, isNaN(m) ? null : m];
  }, [field.value]);

  const [draftHour, setDraftHour] = useState(openHour ?? 9);
  const [draftMinute, setDraftMinute] = useState(openMinute ?? 0);

  useEffect(() => {
    if (openHour != null) setDraftHour(openHour);
    if (openMinute != null) setDraftMinute(openMinute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);
  const minutes = useMemo(
    () =>
      Array.from(
        { length: Math.floor(60 / stepMinutes) },
        (_, i) => i * stepMinutes
      ),
    [stepMinutes]
  );

  const minM = toMinutes(minTime);
  const maxM = toMinutes(maxTime);

  const isTimeAllowed = (h, m) => {
    const t = h * 60 + m;

    if (minM != null) {
      if (minExclusive ? t <= minM : t < minM) return false;
    }
    if (maxM != null) {
      if (maxExclusive ? t >= maxM : t > maxM) return false;
    }
    return true;
  };

  // Disable hour if no minute for that hour is valid
  const isHourDisabled = (h) => !minutes.some((m) => isTimeAllowed(h, m));

  // Disable minute if current hour + this minute is invalid
  const isMinuteDisabled = (m) => !isTimeAllowed(draftHour, m);

  // If constraints change and current draft is invalid, snap to first allowed time
  useEffect(() => {
    if (!minutes.length) return;
    if (isTimeAllowed(draftHour, draftMinute)) return;

    for (let h = 0; h < 24; h++) {
      for (const m of minutes) {
        if (isTimeAllowed(h, m)) {
          setDraftHour(h);
          setDraftMinute(m);
          return;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minTime, maxTime, minExclusive, maxExclusive, stepMinutes]);

  const commit = (closePopover) => {
    if (!isTimeAllowed(draftHour, draftMinute)) return;

    field.onChange(`${pad(draftHour)}:${pad(draftMinute)}`);
    field.onBlur(); // ✅ important for mode: onTouched
    closePopover?.();
  };

  const clear = (closePopover) => {
    field.onChange('');
    field.onBlur();
    closePopover?.();
  };

  const display = field.value ? field.value : '';

  return (
    <Popover
      className={`relative ${disabled ? 'opacity-60 pointer-events-none' : ''} ${className}`}
    >
      <PopoverButton
        className={`w-full border border-gray-300 rounded-lg shadow-sm p-2 text-sm text-left outline-none
        focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white`}
      >
        {display || <span className="text-gray-400">{placeholder}</span>}
      </PopoverButton>

      {/* ✅ Error below the input (visible even when popover closed) */}
      {fieldState.error && (
        <p className="mt-1 text-xs text-red-500">{fieldState.error.message}</p>
      )}

      <Transition
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <PopoverPanel className="absolute z-50 mt-2 w-[220px] rounded-xl border border-gray-200 bg-white p-3 shadow-xl">
          {({ close }) => (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">HH : MM</span>

                {/* optional quick picks - only commit if allowed */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (!isTimeAllowed(0, 0)) return;
                      setDraftHour(0);
                      setDraftMinute(0);
                      commit(close);
                    }}
                    className="text-xs px-2 py-1 rounded-md bg-gray-100"
                  >
                    00:00
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      // snap to a minute that exists in list (e.g. if stepMinutes=5, 59 not present)
                      const lastMinute = minutes[minutes.length - 1] ?? 55;
                      if (!isTimeAllowed(23, lastMinute)) return;
                      setDraftHour(23);
                      setDraftMinute(lastMinute);
                      commit(close);
                    }}
                    className="text-xs px-2 py-1 rounded-md bg-gray-100"
                  >
                    23:59
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <TimeColumn
                  items={hours}
                  selected={draftHour}
                  onSelect={setDraftHour}
                  isDisabled={isHourDisabled}
                />
                <div className="flex items-center text-gray-400">:</div>
                <TimeColumn
                  items={minutes}
                  selected={draftMinute}
                  onSelect={setDraftMinute}
                  isDisabled={isMinuteDisabled}
                />
              </div>

              <div className="mt-3 flex justify-between">
                <button
                  type="button"
                  onClick={() => clear(close)}
                  className="text-sm px-3 py-1.5 rounded-md bg-gray-100"
                >
                  Clear
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => close()}
                    className="text-sm px-3 py-1.5 rounded-md bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => commit(close)}
                    className="text-sm px-3 py-1.5 rounded-md bg-yellow-500 text-white"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </>
          )}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
};

export default TimePicker;
